import { useEffect } from 'react';
import { css } from '@emotion/react';
import { PageTitle } from '@frontend/components';
import { useTranslation } from '@frontend/i18n';
import { useGetInvoiceQuery } from '@frontend/payments-invoice-controller';
import { breakpoints } from '@frontend/responsiveness';
import { useMatch, useNavigate } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import { useModalControl } from '@frontend/design-system';
import { InvoiceStatusChip } from '../../components/Invoices/InvoiceStatusChip';
import { PaymentRequestActions } from '../../components/PaymentRequestDetails/payment-request-actions';
import { PaymentRequestDetailsPage } from '../../components/PaymentRequestDetails/payment-request-details-page';
import { PaymentsPage } from '../../components/PaymentsPage';

const styles = {
  chipStyle: css`
    margin-left: ${theme.spacing(1)};
    @container (max-width: ${breakpoints.small.max}px) {
      margin-left: 0;
      margin-top: ${theme.spacing(1)};
    }
  `,
};

export const PaymentRequestDetails = ({ invoiceId }: { invoiceId: string | undefined }) => {
  const { t } = useTranslation('payments');

  const { invoice } = useGetInvoiceQuery(invoiceId, 1);

  const collectModalControl = useModalControl();
  const navigate = useNavigate();

  const isRefundPage = !!useMatch({ from: '/payments/refunds', shouldThrow: false });
  useEffect(() => {
    //redirect to refunds tab of the payment requests page
    if (!invoiceId && isRefundPage) {
      navigate({ to: '/payments/invoices', search: { status: 'refunded' } });
    }
  }, [invoiceId, isRefundPage, navigate]);

  return (
    <PaymentsPage
      title={
        <>
          <PageTitle title={t('Payment Request')} />
          {invoice && <InvoiceStatusChip invoice={invoice} css={styles.chipStyle} />}
        </>
      }
      breadcrumbs={
        !isRefundPage
          ? [
              {
                label: t('Request History'),
                to: '/payments/invoices',
              },
              { label: t('Payment Details') },
            ]
          : [
              {
                label: t('Request History'),
                to: '/payments/invoices?status=refunded',
              },
              { label: t('Refund Details') },
            ]
      }
      action={invoice && <PaymentRequestActions invoice={invoice} collectModalControl={collectModalControl} />}
    >
      <PaymentRequestDetailsPage selectedInvoiceId={invoiceId} />
    </PaymentsPage>
  );
};
